/**
 * PATH: apps/quote/src/pages/claims/claims-list-page/ClaimsListPage.tsx
 * FILE_ID: QUOTE-CLAIMS-LIST-FRAME-001
 * PURPOSE: Orchestrator for payment claims list page
 */

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { FileCog, FileText, Plus, Search, Filter, XCircle, RefreshCw } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { useApi } from '@/lib/setupApi'
import type { PaymentClaim, ClaimsListResponse } from './types'
import { ClaimRow } from './ClaimRow'
import { ClaimProfileAdmin } from '../ClaimProfileAdmin'

function claimSequence(claim: PaymentClaim) {
  return (claim as PaymentClaim & { contract_sequence?: number | null })
    .contract_sequence ?? claim.claim_number
}

export default function ClaimsListPage() {
  const api = useApi()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const { data, isLoading, error, refetch } = useQuery<ClaimsListResponse>({
    queryKey: ['claims', statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (statusFilter !== 'all') params.append('status', statusFilter)
      return (await api.get(`/api/claims/?${params.toString()}`)).data
    },
  })

  const filteredClaims = useMemo(() => {
    if (!data?.results) return []
    if (!searchQuery.trim()) return data.results
    const query = searchQuery.toLowerCase()
    return data.results.filter(claim =>
      claim.job.name.toLowerCase().includes(query) || claim.job.job_number.toLowerCase().includes(query) ||
      claim.job.client_name.toLowerCase().includes(query) || claim.period.toLowerCase().includes(query) ||
      `claim #${claimSequence(claim)}`.includes(query)
    )
  }, [data?.results, searchQuery])

  const handleAction = async (action: string, claim: PaymentClaim) => {
    switch (action) {
      case 'view': case 'edit': navigate(`/claims/${claim.id}`); break
      case 'submit': try { await api.post(`/api/claims/${claim.id}/submit/`); refetch() } catch (err) { console.error('Failed to submit claim:', err) } break
      case 'approve': try { await api.post(`/api/claims/${claim.id}/approve/`); refetch() } catch (err) { console.error('Failed to approve claim:', err) } break
      case 'send_xero': try { await api.post(`/api/claims/${claim.id}/send_to_xero/`); refetch() } catch (err) { console.error('Failed to send to Xero:', err) } break
      case 'delete': if (confirm('Are you sure you want to delete this claim?')) { try { await api.delete(`/api/claims/${claim.id}/`); refetch() } catch (err) { console.error('Failed to delete claim:', err) } } break
    }
  }

  const stats = useMemo(() => {
    if (!data?.results) return { total: 0, draft: 0, pending: 0, approved: 0, totalValue: 0 }
    return {
      total: data.results.length,
      draft: data.results.filter(c => c.status === 'draft').length,
      pending: data.results.filter(c => ['submitted', 'under_review'].includes(c.status)).length,
      approved: data.results.filter(c => ['approved', 'sent', 'paid'].includes(c.status)).length,
      totalValue: data.results.reduce((sum, c) => sum + parseFloat(c.this_claim), 0),
    }
  }, [data?.results])

  if (params.get('view') === 'profiles') return <ClaimProfileAdmin />

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div><h1 className="text-2xl font-bold text-slate-900 dark:text-white">Payment Claims</h1><p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Manage progress claims, assessments, evidence and issued documents</p></div>
            <div className="flex flex-wrap gap-2">
              <Link to="/claims?view=profiles" className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 px-4 text-sm font-medium"><FileCog className="h-5 w-5" />Profiles</Link>
              <Link to="/claims/new" className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-amber-500 px-4 font-medium text-white transition-colors hover:bg-amber-600"><Plus className="h-5 w-5" />New Claim</Link>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4"><div className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</div><div className="text-sm text-slate-600 dark:text-slate-400">Total Claims</div></div>
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4"><div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.draft}</div><div className="text-sm text-slate-600 dark:text-slate-400">Draft</div></div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4"><div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.pending}</div><div className="text-sm text-slate-600 dark:text-slate-400">Pending Approval</div></div>
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4"><div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(stats.totalValue)}</div><div className="text-sm text-slate-600 dark:text-slate-400">Total Claimed</div></div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input type="text" placeholder="Search jobs, clients, or claims..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none" />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="pl-10 pr-8 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm appearance-none cursor-pointer focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none">
              <option value="all">All Status</option>
              <option value="draft">Draft</option><option value="submitted">Submitted</option><option value="under_review">Under Review</option>
              <option value="approved">Approved</option><option value="sent">Sent</option><option value="paid">Paid</option><option value="rejected">Rejected</option>
            </select>
          </div>
          <button onClick={() => refetch()} className="p-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors" title="Refresh"><RefreshCw className="w-5 h-5 text-slate-600 dark:text-slate-400" /></button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" /></div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 text-red-500"><XCircle className="w-12 h-12 mb-3" /><p className="font-medium">Failed to load claims</p><button onClick={() => refetch()} className="mt-4 text-sm text-amber-600 hover:text-amber-700">Try again</button></div>
          ) : filteredClaims.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500"><FileText className="w-12 h-12 mb-3" /><p className="font-medium">No claims found</p><p className="text-sm mt-1">{searchQuery ? 'Try a different search term' : 'Create your first claim to get started'}</p></div>
          ) : (
            <>
            <div className="divide-y divide-slate-200 sm:hidden dark:divide-slate-700">
              {filteredClaims.map((claim) => (
                <Link key={claim.id} to={`/claims/${claim.id}`} className="block min-h-24 p-4 hover:bg-slate-50 dark:hover:bg-slate-700">
                  <div className="flex items-start justify-between gap-3"><span className="font-semibold">Claim #{claimSequence(claim)}</span><span className="rounded-full bg-slate-100 px-2 py-1 text-xs capitalize">{claim.status.replace('_', ' ')}</span></div>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{claim.job.job_number} · {claim.job.name}</p>
                  <div className="mt-2 flex justify-between text-xs text-slate-500"><span>{claim.period}</span><span className="font-mono font-medium text-slate-800 dark:text-slate-100">{formatCurrency(Number(claim.this_claim_incl_gst))}</span></div>
                </Link>
              ))}
            </div>
            <table className="hidden w-full sm:table">
              <thead><tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Claim / Job</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Period</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">This Claim</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Progress</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Total Claimed</th>
                <th className="px-4 py-3 w-16"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredClaims.map((claim) => <ClaimRow key={claim.id} claim={claim} onAction={handleAction} />)}
              </tbody>
            </table>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
